'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
  }
}

interface CrispChatProps {
  welcomeMessage?: string;
}

export default function CrispChat({ welcomeMessage }: CrispChatProps) {
  const { user, userProfile } = useAuth();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    const websiteId = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID;
    if (!websiteId || websiteId === 'your-crisp-website-id-here') return;

    // Prevent double-loading if already initialized
    if (scriptLoadedRef.current || document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      scriptLoadedRef.current = true;
      return;
    }

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = websiteId;

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;

    script.onload = () => {
      scriptLoadedRef.current = true;

      // Set welcome message after script loads
      if (welcomeMessage) {
        window.$crisp.push(['set', 'message:text', [welcomeMessage]]);
      }

      // Pre-fill user data if already authenticated when script loads
      const email = userProfile?.email || user?.email;
      const name = userProfile?.fullName;

      if (email) {
        window.$crisp.push(['set', 'user:email', [email]]);
      }
      if (name) {
        window.$crisp.push(['set', 'user:nickname', [name]]);
      }
    };

    document.head.appendChild(script);

    return () => {
      // Only clean up the script tag; do not reset $crisp to avoid breaking active sessions
      const existingScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (existingScript) existingScript.remove();
      scriptLoadedRef.current = false;
    };
  }, []);

  // Update user identity whenever auth state changes (login/logout)
  useEffect(() => {
    const websiteId = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID;
    if (!websiteId || websiteId === 'your-crisp-website-id-here') return;
    if (!scriptLoadedRef.current || !window.$crisp) return;

    const email = userProfile?.email || user?.email;
    const name = userProfile?.fullName;

    if (email) {
      window.$crisp.push(['set', 'user:email', [email]]);
    }
    if (name) {
      window.$crisp.push(['set', 'user:nickname', [name]]);
    }

    // Reset identity on logout
    if (!user) {
      window.$crisp.push(['do', 'session:reset']);
    }
  }, [user, userProfile]);

  return null;
}
